import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { LogoCustomizationAsset } from '@template-project/afrodita-components';
import { createBffClient } from '@/lib/bff/create-storefront-client';
import { useIntl } from '@/lib/intl';
import type { HomePageContentBlock } from '@template-project/storefront-bff-client';

const GLOBAL_CHROME_STALE_MS = 60_000;

export interface AnnouncementBarCmsProps {
  message: string;
  backgroundColor?: string;
  textColor?: string;
  linkText?: string;
  linkHref?: string;
  dismissible?: boolean;
}

export interface HeaderBrandingCmsProps {
  siteTitle?: string;
  logo?: LogoCustomizationAsset;
}

export interface FooterLinkCmsProps {
  text: string;
  href: string;
}

export interface FooterColumnCmsProps {
  title: string;
  links: FooterLinkCmsProps[];
}

export interface FooterCmsProps {
  brandName?: string;
  columns?: FooterColumnCmsProps[];
}

function blockPayload<T>(
  blocks: HomePageContentBlock[],
  type: string,
): T | undefined {
  const block = blocks.find((entry) => entry.type === type);
  if (!block?.payload || typeof block.payload !== 'object') return undefined;
  return block.payload as T;
}

function localizeStorefrontHref(href: string, localizedPath: (path: string) => string): string {
  const trimmed = href.trim();
  if (!trimmed) return trimmed;
  if (
    trimmed.startsWith('http://')
    || trimmed.startsWith('https://')
    || trimmed.startsWith('#')
    || trimmed.startsWith('mailto:')
  ) {
    return trimmed;
  }
  if (trimmed.startsWith('/')) {
    return localizedPath(trimmed);
  }
  return localizedPath(`/${trimmed}`);
}

export function useGlobalChrome() {
  const { locale, localizedPath } = useIntl();

  const query = useQuery({
    queryKey: ['global-chrome', locale],
    queryFn: async () => {
      const client = createBffClient();
      const response = await client.getGlobalChrome({ locale });
      return response.data.blocks;
    },
    staleTime: GLOBAL_CHROME_STALE_MS,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const blocks = query.data ?? [];
  const hasAnnouncementBlock = blocks.some((entry) => entry.type === 'announcementBar');
  const hasHeaderBrandingBlock = blocks.some((entry) => entry.type === 'headerBranding');
  const hasFooterBlock = blocks.some((entry) => entry.type === 'footer');

  const announcementRaw = blockPayload<{
    message?: string;
    backgroundColor?: string;
    textColor?: string;
    linkText?: string;
    linkHref?: string;
    dismissible?: boolean;
  }>(blocks, 'announcementBar');

  const headerRaw = blockPayload<{
    siteTitle?: string;
    logoSrc?: string;
    logoAlt?: string;
    logoWidth?: number;
    logoHeight?: number;
  }>(blocks, 'headerBranding');

  const footerRaw = blockPayload<{
    brandName?: string;
    columns?: Array<{ title: string; links: Array<{ text: string; href: string }> }>;
  }>(blocks, 'footer');

  const announcement: AnnouncementBarCmsProps | undefined =
    announcementRaw?.message?.trim()
      ? {
          message: announcementRaw.message.trim(),
          backgroundColor: announcementRaw.backgroundColor?.trim() || undefined,
          textColor: announcementRaw.textColor?.trim() || undefined,
          linkText: announcementRaw.linkText?.trim() || undefined,
          linkHref: announcementRaw.linkHref?.trim()
            ? localizeStorefrontHref(announcementRaw.linkHref, localizedPath)
            : undefined,
          dismissible: announcementRaw.dismissible,
        }
      : undefined;

  const headerBranding: HeaderBrandingCmsProps | undefined =
    hasHeaderBrandingBlock && headerRaw
      ? {
          siteTitle: headerRaw.siteTitle?.trim() || undefined,
          logo: headerRaw.logoSrc?.trim()
            ? {
                src: headerRaw.logoSrc.trim(),
                alt: headerRaw.logoAlt?.trim() || headerRaw.siteTitle?.trim() || 'Store logo',
                width: headerRaw.logoWidth,
                height: headerRaw.logoHeight,
              }
            : undefined,
        }
      : undefined;

  const footer: FooterCmsProps | undefined =
    hasFooterBlock && footerRaw
      ? {
          brandName: footerRaw.brandName?.trim() || undefined,
          columns: footerRaw.columns?.map((column) => ({
            title: column.title,
            links: column.links.map((link) => ({
              text: link.text,
              href: localizeStorefrontHref(link.href, localizedPath),
            })),
          })),
        }
      : undefined;

  return {
    ...query,
    blocks,
    hasAnnouncementBlock,
    hasHeaderBrandingBlock,
    hasFooterBlock,
    announcement,
    headerBranding,
    footer,
  };
}
